import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { Icon } from "@iconify/react";

import {
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  getGrantStrikePrice,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import Tooltip from "../../../components/shared/Tooltip";
import SearchDropDown from "../../../components/shared/SearchDropdown";
import {
  GrantedBy,
  useGrantDetailsStore,
} from "../../../store/useGrantDetailsStore";
import { useValuationDetails } from "../../../queries/Valuation";
import { Valuation, ValuationSource } from "../../../types/ValuationType";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { Select } from "../../../components/shared/Select";
import { SummaryMetricCard } from "../../../components/shared/SummaryMetricCard";
import { useGetGrantPPS } from "../../../queries";
import { useCompanyStore } from "../../../store";
import { RoundingSettings, StrikePriceType } from "../../../types/EsopPlan";
import AllocationEnvelopePanel from "./AllocationEnvelopePanel";
import { useGetAllMatrices } from "../../../queries/allocationMatrix";
import { useGetGrantLetterSettings } from "../../../queries/siteSettings";
import { useRegionalGrantRules } from "../../../queries/onBoarding";
import { resolveRegionalGrantRule } from "../../../utils/regionalGrantRules";
import {
  AllocationMode,
  CostOrGain,
} from "../../../store/useAllocationMatrixStore";
import {
  roundNumberToNearestMultiple,
  applyRoundingSettings,
} from "../../../utils/string";

interface GrantByOptionsProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
}

enum ChangedValue {
  Options = "Options",
  Amount = "Amount",
  Valuation = "Valuation",
  Share = "Share",
}

function calculateOptionsFromAmount(
  amount: number,
  sharePrice: number,
  grantPrice: number,
  isFractional: boolean,
  costOrGain: CostOrGain | null | undefined,
  useAllocationFormula: boolean,
  conversionShares: number = 1,
  roundingSettings: RoundingSettings | null = null
): number {
  let rawOptions: number;

  if (useAllocationFormula && sharePrice > 0) {
    // Use allocation formula based on costOrGain
    if (costOrGain === CostOrGain.GAIN) {
      const denominator = (sharePrice - grantPrice) * conversionShares;
      if (denominator <= 0) return 0;
      const result = amount / denominator;
      rawOptions = isFractional ? result : parseInt(result.toString(), 10);
    } else {
      // COST formula
      rawOptions = isFractional
        ? parseFloat((amount / (sharePrice * conversionShares)).toFixed(4))
        : parseInt((amount / (sharePrice * conversionShares)).toString(), 10);
    }
  } else {
    // Fallback: original behavior with conversion shares
    if (sharePrice <= 0) return 0;
    const divisor = sharePrice * conversionShares;
    rawOptions = isFractional
      ? parseFloat((amount / divisor).toFixed(4))
      : parseInt((amount / divisor).toString(), 10);
  }

  // Apply plan's rounding settings only if they exist
  if (roundingSettings) {
    return applyRoundingSettings(rawOptions, roundingSettings);
  }
  return rawOptions;
}

export const GrantPageCard = ({
  header,
  value,
}: {
  header: string;
  value: string;
}) => <SummaryMetricCard label={header} value={value} />;

const GrantByOptionsPage = (props: GrantByOptionsProps) => {
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const data = useGrantDetailsStore();
  const { data: grantSettings } = useGetGrantLetterSettings();
  const useAllocationMatrix =
    grantSettings?.useAllocationMatrixForGranting ?? false;
  const { data: matrices } = useGetAllMatrices();
  const matrixName =
    (matrices ?? []).find((m: any) => m.id === data.allocationMatrixId)?.name ||
    "—";
  const {
    data: _valuationData,
    isPlaceholderData,
    isFetched,
  } = useValuationDetails();
  const [valuationData, setValuationData] = useState<Valuation[]>([]);
  const [errors, setErrors] = useState<any>({});
  useEffect(() => {
    if (_valuationData && _valuationData.length > 0 && !isPlaceholderData) {
      const companyValuations = _valuationData.filter(
        (v) => v.valuationSource === ValuationSource.COMPANY
      );
      setValuationData([...companyValuations]);
    }
  }, [isFetched, _valuationData, isPlaceholderData]);

  useEffect(() => {
    if (
      data.grantedBy === GrantedBy.AMOUNT &&
      data.amount > 0 &&
      data.sharePrice > 0
    ) {
      const useAllocationFormula: boolean = !!(
        useAllocationMatrix &&
        data.matchedRule &&
        (data.matchedRule.allocationMode === AllocationMode.FIXED_OPTIONS ||
          data.matchedRule.allocationMode === AllocationMode.ABSOLUTE_VALUE)
      );

      if (useAllocationFormula) {
        data.setOptionsGranted(
          calculateOptionsFromAmount(
            data.amount,
            data.sharePrice,
            data.grantPrice || 0,
            data?.plan?.isFractional ?? false,
            data.matchedRule?.costOrGain,
            useAllocationFormula,
            data.plan?.conversionShares || 1,
            data.plan?.roundingSettings
          )
        );
      }
    }
  }, [
    data.amount,
    data.grantPrice,
    data.sharePrice,
    data.grantedBy,
    data.plan?.isFractional,
    data.plan?.conversionShares,
    data.plan?.roundingSettings,
    useAllocationMatrix,
    data.matchedRule,
  ]);

  const { companyData } = useCompanyStore();
  const { data: regionalRuleCatalog } = useRegionalGrantRules();
  const regionalRule = resolveRegionalGrantRule(
    regionalRuleCatalog?.rules,
    companyData?.companyMetaData?.registeredCountry
  );
  const isStrikePriceOptional =
    data.plan?.planType === "ESOP" &&
    regionalRule.strikePriceOptional !== undefined;

  function handleChange(value: number, changedValue: ChangedValue) {
    if (changedValue === ChangedValue.Options) {
      data.setOptionsGranted(value);
      if (value >= 0 && (data.sharePrice || 0) >= 0) {
        data.setValueOfOptions(
          parseFloat(
            (
              value *
              ((data.sharePrice || 0) * (data.plan?.conversionShares || 1))
            ).toFixed(4)
          )
        );
      }
    } else if (changedValue === ChangedValue.Amount) {
      data.setAmount(value);
      if (data.amount >= 0 && data.sharePrice >= 0) {
        const useAllocationFormula: boolean = !!(
          useAllocationMatrix &&
          data.matchedRule &&
          (data.matchedRule.allocationMode === AllocationMode.FIXED_OPTIONS ||
            data.matchedRule.allocationMode === AllocationMode.ABSOLUTE_VALUE)
        );

        data.setOptionsGranted(
          calculateOptionsFromAmount(
            value,
            data.sharePrice,
            data.grantPrice || 0,
            data?.plan?.isFractional ?? false,
            useAllocationFormula ? data.matchedRule?.costOrGain : null,
            useAllocationFormula,
            data.plan?.conversionShares || 1,
            data.plan?.roundingSettings
          )
        );
      }
    } else if (changedValue === ChangedValue.Share) {
      data.setSharePrice(value);
      if (
        value >= 0 &&
        (data.optionsGranted || 0) >= 0 &&
        data.grantedBy === GrantedBy.OPTIONS
      ) {
        data.setValueOfOptions(
          parseFloat(
            (
              data.optionsGranted *
              ((value || 0) * (data.plan?.conversionShares || 1))
            ).toFixed(4)
          )
        );
      }
      if (
        value >= 0 &&
        (data.amount || 0) >= 0 &&
        data.grantedBy === GrantedBy.AMOUNT
      ) {
        const useAllocationFormula: boolean = !!(
          useAllocationMatrix &&
          data.matchedRule &&
          (data.matchedRule.allocationMode === AllocationMode.FIXED_OPTIONS ||
            data.matchedRule.allocationMode === AllocationMode.ABSOLUTE_VALUE)
        );

        data.setOptionsGranted(
          calculateOptionsFromAmount(
            data.amount,
            value,
            data.grantPrice || 0,
            data?.plan?.isFractional ?? false,
            useAllocationFormula ? data.matchedRule?.costOrGain : null,
            useAllocationFormula,
            data.plan?.conversionShares || 1,
            data.plan?.roundingSettings
          )
        );
      }
    }
  }

  const validationSchema = Yup.object().shape({
    noOfOptions: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .typeError("No of Options must be a number")
      .required("No of Options is required")
      .min(
        data?.plan?.isFractional ? 0.0001 : 1,
        `No of Options must be greater than ${
          data?.plan?.isFractional ? 0.0001 : 1
        }`
      )
      .max(
        data?.plan?.optionsReserved,
        "No. of Options must be lesser than the Options Available"
      ),

    grantPrice: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .typeError("Grant Price must be a number")
      // .test(
      //   "grant-price-required",
      //   "Grant Price is required",
      //   (value) =>
      //     isStrikePriceOptional || (value !== undefined && value !== null)
      // )
      // .test(
      //   "grantPrice-min-conditional",
      //   "Grant Price must be greater than 0 for ESOP plans",
      //   function grantPriceMinConditional(value) {
      //     // If value is empty, let .required() handle it
      //     if (value === undefined || value === null) return true;

      //     const planType = this.parent?.plan?.planType;
      //     if (planType === "ESOP") {
      //       return value > 0;
      //     }
      //     return value >= 0; // zero allowed for non-ESOP
      //   }
      // )
      // .min(0, "Grant Price cannot be less than 0")
      .test(
        "regional-strike-price-required",
        `Grant Price must be greater than 0 for ESOP plans`,
        (value) => {
          if (value === undefined || value === null) return true;
          if (data.plan?.planType === "RSU") return value >= 0.0001;
          if (data.plan?.planType !== "ESOP") return value >= 0;
          return isStrikePriceOptional || value > 0;
        }
      )
      .test(
        "regional-grant-price-minimum",
        `Grant Price must be at least ${regionalRule.minStrikePrice}`,
        (value) =>
          value === undefined ||
          value === null ||
          data.plan?.planType !== "ESOP" ||
          isStrikePriceOptional ||
          regionalRule.minStrikePrice === undefined ||
          value >= regionalRule.minStrikePrice
      ),
  });

  const formik = useFormik({
    initialValues: {},
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  function handleSubmit() {
    if (
      data.grantedBy === GrantedBy.AMOUNT &&
      useAllocationMatrix &&
      !(data.sharePrice > 0)
    ) {
      setErrors({ sharePrice: "Share price is required" });
      return;
    }

    const formValues = {
      noOfOptions: data.optionsGranted || 0,
      grantedBy: data.grantedBy,
      grantPrice:
        grantPriceInput === "" ? undefined : parseFloat(grantPriceInput),
      plan: {
        isFractional: data.plan?.isFractional,
        planType: data.plan?.planType,
        optionsReserved: data.plan?.optionsReserved,
      },
    };

    validationSchema
      .validate(formValues, { abortEarly: false })
      .then(() => {
        props.onStepChange();
      })
      .catch((err) => {
        const formattedErrors = err.inner.reduce(
          (acc: Record<string, string>, curr: Yup.ValidationError) => {
            if (curr.path) acc[curr.path] = curr.message;
            return acc;
          },
          {}
        );
        setErrors(formattedErrors);
      });
  }

  const [inputValue, setInputValue] = useState("");
  const [grantPriceInput, setGrantPriceInput] = useState("");
  const [isGrantPriceEntered, setIsGrantPriceEntered] = useState(false);
  useEffect(() => {
    if (grantPriceInput === "") {
      setIsGrantPriceEntered(false);
      return;
    }

    const parsed = parseFloat(grantPriceInput);
    if (!Number.isNaN(parsed)) {
      data.setGrantPrice(parsed);
      setIsGrantPriceEntered(true);
    }
  }, [grantPriceInput]);

  useEffect(() => {
    if (data.grantPrice != null) {
      setGrantPriceInput(data.grantPrice.toString());
    } else {
      setGrantPriceInput("");
    }
  }, [data.grantPrice]);

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Grant Information
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          {data.grantedBy === GrantedBy.OPTIONS
            ? "Enter the details for the grant, including the number of options to be granted, based on the available options, conversion ratio, and plan type."
            : "Enter the details of the grant, including the grant amount, based on the available options, conversion ratio, and plan type."}
        </HStack>
      </VStack>
      <HStack className="gap-4 p-4">
        <GrantPageCard
          header="Options Available"
          value={(data?.plan?.optionsReserved || 0).toLocaleString(
            currencyType,
            {
              maximumFractionDigits: 4,
            }
          )}
        />
        <GrantPageCard header="Conversion Ratio" value={data.conversionRatio} />
        <GrantPageCard header="Plan Type" value={data?.plan?.planType} />
        {useAllocationMatrix && data.allocationMatrixId && (
          <GrantPageCard header="Allocation Matrix" value={matrixName} />
        )}
      </HStack>
      {useAllocationMatrix && <AllocationEnvelopePanel />}
      <VStack className="gap-4 px-6">
        <HStack></HStack>
        <HStack className="items-center justify-between gap-8 pr-40 text-center"></HStack>
        <HStack className="gap-8 ">
          {data.grantedBy === GrantedBy.OPTIONS && (
            <div className="flex-1">
              <Label className="required text-sm font-normal">
                No. of Options
              </Label>
              <Input
                type="number"
                placeholder={
                  data.optionsGranted <= 0 && !inputValue
                    ? "Enter no. of options"
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  const isFractionalAllowed = data?.plan.isFractional;
                  if (
                    (isFractionalAllowed &&
                      (/^\d*\.?\d*$/.test(value) || value === "")) ||
                    (!isFractionalAllowed &&
                      (/^\d*$/.test(value) || value === ""))
                  ) {
                    setInputValue(value);
                    const parsedValue = isFractionalAllowed
                      ? value === ""
                        ? 0
                        : parseFloat(Number(value).toFixed(4))
                      : value === ""
                      ? 0
                      : parseInt(value, 10);

                    handleChange(parsedValue, ChangedValue.Options);
                  }
                }}
                value={
                  inputValue ||
                  (data.optionsGranted !== 0 ? data.optionsGranted : "")
                }
              />
              {errors.noOfOptions && <Error text={errors.noOfOptions} />}
            </div>
          )}
          {data.grantedBy === GrantedBy.AMOUNT && (
            <div className="flex-1">
              <Label className="text-sm font-normal">
                Grant Amount ({currencySymbol})
              </Label>
              <Input
                type="number"
                placeholder={`${data.amount > 0 ? "" : "Enter the amount"}`}
                onChange={(e) => {
                  const value = parseFloat(Number(e.target.value).toFixed(4));
                  handleChange(value, ChangedValue.Amount);
                }}
                value={data.amount === 0 ? "" : data.amount}
              />
            </div>
          )}
          <div className="flex-1">
            <Label
              className={`${
                isStrikePriceOptional ? "" : "required "
              }text-sm font-normal`}
            >
              Grant Price ({currencySymbol})
            </Label>
            <Input
              type="number"
              value={grantPriceInput}
              onChange={(e) => setGrantPriceInput(e.target.value)}
              placeholder="Enter Grant Price"
            />
            {errors.grantPrice && <Error text={errors.grantPrice} />}
          </div>
        </HStack>
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label className="text-sm font-normal">Valuation Reports</Label>
            <Select
              onChange={(e) => {
                if (companyData?.companyMetaData.typeOfTheCompany === "PUBLIC")
                  return;
                const valuation = valuationData.find(
                  (v) => v.id === e.target.value
                );
                data.setValuationId(e.target.value);
                handleChange(valuation?.pps || 0, ChangedValue.Share);
              }}
              placeholder="--Select--"
              options={valuationData}
              value={data.valuationId}
              textGetter={(e) => e.name}
              valueGetter={(e) => e.id}
              className={""}
              disabled={
                companyData?.companyMetaData.typeOfTheCompany === "PUBLIC"
              }
            />
          </div>
          <div className="flex-1">
            <Label className="text-sm font-normal">
              Share Price ({currencySymbol})
            </Label>
            <Input
              type="number"
              disabled={data.valuationId !== ""}
              placeholder={`${data.sharePrice > 0 ? "" : "Enter share price"}`}
              min={0}
              onChange={(e) => {
                const value = parseFloat(
                  Number(e.target.value === "" ? "0" : e.target.value).toFixed(
                    4
                  )
                );
                handleChange(value, ChangedValue.Share);
              }}
              value={data.sharePrice === 0 ? "" : data.sharePrice}
            />
            {errors.sharePrice && <Error text={errors.sharePrice} />}
          </div>
        </HStack>
        {data.grantedBy === GrantedBy.OPTIONS && (
          <HStack className="items-start gap-8">
            {((data.plan?.planType === "ESOP" &&
              isGrantPriceEntered &&
              data.grantPrice > 0 &&
              data.optionsGranted > 0 &&
              data.sharePrice > 0) ||
              (data.plan?.planType !== "ESOP" &&
                isGrantPriceEntered &&
                data.optionsGranted > 0 &&
                data.sharePrice > 0)) && (
              <div className="flex items-center flex-1 gap-x-2">
                <Label className="text-sm font-normal">
                  Cost of Grant ({currencySymbol}):
                </Label>
                <p className="mb-2 text-sm">
                  {(
                    data.optionsGranted *
                    getGrantStrikePrice(
                      data.grantPrice,
                      data.plan?.conversionShares || 1,
                      data.plan?.strikePriceType ||
                        StrikePriceType.OPTIONS_PRICE
                    )
                  ).toLocaleString(currencyType)}
                </p>
              </div>
            )}
            {/* Value of Shares Section */}
            {data.sharePrice > 0 &&
              isGrantPriceEntered &&
              data.optionsGranted > 0 && (
                <div className="flex items-center flex-1 gap-x-2">
                  <Label className="text-sm font-normal">
                    <span>Value at Grant ({currencySymbol}):</span>
                  </Label>
                  <HStack className="gap-1">
                    <p className="mb-2 text-sm">
                      {(
                        data.optionsGranted *
                        data.sharePrice *
                        (data.plan?.conversionShares || 1)
                      ).toLocaleString(currencyType)}
                    </p>
                    <Tooltip
                      text={`Calculated as No. of Options × Share Price × Conversion Ratio (if any).`}
                      _className="w-96"
                    >
                      <Icon
                        icon="material-symbols:info-outline-rounded"
                        height={18}
                        width={18}
                        className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark items-center"
                      />
                    </Tooltip>
                  </HStack>
                </div>
              )}
          </HStack>
        )}

        {data.grantedBy === GrantedBy.AMOUNT &&
          isGrantPriceEntered &&
          data.sharePrice > 0 &&
          data.amount > 0 && (
            <HStack className="gap-8 ">
              <div className="flex-1">
                <HStack className="items-center gap-1 mb-1">
                  <Label className="required text-sm font-normal">
                    No. of Options
                  </Label>
                  {data.plan?.roundingSettings && (
                    <Tooltip
                      text={`Value will be rounded ${data.plan.roundingSettings.strategy
                        .replace("ROUND_", "")
                        .toLowerCase()} to the nearest ${
                        data.plan.roundingSettings.multiple
                      }.`}
                      _className="w-96"
                    >
                      <Icon
                        icon="material-symbols:info-outline-rounded"
                        height={16}
                        width={16}
                        className="text-slate-dark cursor-pointer mb-1.5"
                      />
                    </Tooltip>
                  )}
                </HStack>
                <Input
                  type="number"
                  placeholder={`${
                    data.optionsGranted <= 0 ? "Enter no. of options" : ""
                  }`}
                  onChange={(e) => {}}
                  disabled={true}
                  value={data.optionsGranted}
                />
                {errors.noOfOptions && <Error text={errors.noOfOptions} />}
              </div>
              <div className="flex items-center flex-1 mt-6 gap-x-2">
                <Label className="text-sm font-normal">
                  Cost of Grant ({currencySymbol}):
                </Label>
                <p className="mb-2 text-sm">
                  {(
                    data.optionsGranted *
                    getGrantStrikePrice(
                      data.grantPrice,
                      data.plan?.conversionShares || 1,
                      data.plan?.strikePriceType ||
                        StrikePriceType.OPTIONS_PRICE
                    )
                  ).toLocaleString(currencyType)}
                </p>
              </div>
            </HStack>
          )}

        {data.grantedBy === GrantedBy.AMOUNT &&
          data.amount > 0 &&
          data.sharePrice > 0 &&
          data.optionsGranted > 0 &&
          data.optionsGranted *
            data.sharePrice *
            (data.plan?.conversionShares || 1) <
            data.amount && (
            <HStack className="p-2 text-blue-800 bg-blue-100 rounded">
              <div className="pt-0">
                <Icon
                  icon="material-symbols:info-outline-rounded"
                  height={24}
                  width={24}
                  className="w-full text-xs font-medium text-blue-800 rounded cursor-pointer"
                />
              </div>
              <div className="px-2 pt-1">
                {`Your entered amount of ${formatCurrency(
                  data.amount,
                  currencyType
                )} has been adjusted to ${
                  data.optionsGranted
                } option(s) based on the share price of ${formatCurrency(
                  data.sharePrice,
                  currencyType
                )}. The effective grant amount is ${formatCurrency(
                  data.optionsGranted *
                    data.sharePrice *
                    (data.plan?.conversionShares || 1),
                  currencyType
                )}.`}
              </div>
            </HStack>
          )}

        {data.sharePrice > 0 && isGrantPriceEntered && data.optionsGranted > 0 && (
          <HStack className="p-2 text-blue-800 bg-blue-100 rounded">
            <div className="pt-0">
              <Icon
                icon="material-symbols:info-outline-rounded"
                height={24}
                width={24}
                className="w-full text-xs font-medium text-blue-800 rounded cursor-pointer"
              />
            </div>
            <div className="px-2 pt-1">
              {`The Net Value of the Grant is
              ${formatCurrency(
                data.optionsGranted *
                  data.sharePrice *
                  (data.plan?.conversionShares || 1) -
                  data.optionsGranted *
                    getGrantStrikePrice(
                      data.grantPrice,
                      data.plan?.conversionShares || 1,
                      data.plan?.strikePriceType ||
                        StrikePriceType.OPTIONS_PRICE
                    ),
                currencyType
              )}
              which is arrived at by subtracting "Cost of Grant" from the "Value
              of Options".`}
            </div>
          </HStack>
        )}

        {useAllocationMatrix && (
          <div>
            <Label className="text-sm font-normal">Remarks (Optional)</Label>
            <TextArea
              placeholder="Add any remarks about this grant, e.g. reasons for exceeding or falling below the allocation rule limits."
              value={data.remarks || ""}
              onChange={(e) => data.setRemarks(e.target.value)}
            />
          </div>
        )}

        <HStack className="justify-between pt-4">
          <ButtonPrimary1 onClick={props.onBackClick}>Back</ButtonPrimary1>
          <ButtonPrimary onClick={handleSubmit}>Next</ButtonPrimary>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default GrantByOptionsPage;
